import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, FileText, Percent, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a Norwegian invoice (faktura) include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A Norwegian invoice (faktura) must include: your business name and address; your organization number (organisasjonsnummer); your MVA (VAT) number if registered; the client name and address; a unique invoice number; the invoice date; an itemized description of goods or services; the net amount, VAT rate (25%, 15%, or 12%), VAT amount, and total including VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the MVA (VAT) rate in Norway?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Norway has three MVA (merverdiavgift / VAT) rates. The standard rate is 25%, which applies to most goods and services. The reduced rate of 15% applies to food and non-alcoholic beverages. A further reduced rate of 12% applies to passenger transport, hotel accommodation, and cultural/sporting events. Exports are zero-rated at 0% MVA.',
      },
    },
    {
      '@type': 'Question',
      name: 'When do Norwegian businesses need to register for MVA?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'MVA registration with Skatteetaten (Norwegian Tax Administration) is compulsory once your annual taxable turnover exceeds NOK 50,000. Below this threshold, you do not need to register or charge MVA. Once registered, you must file bimonthly MVA returns and charge MVA on all taxable supplies.',
      },
    },
    {
      '@type': 'Question',
      name: 'How do Norwegian freelancers invoice international clients?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Norwegian freelancers billing overseas clients can invoice in USD, EUR, GBP, or any agreed currency. Export of services to foreign clients is generally zero-rated for MVA. For EU business clients, include your MVA-nummer and apply the reverse charge mechanism. Use the international invoice type to bill in any currency with your Norwegian bank IBAN.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this invoice generator free for Norwegian users?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is completely free for Norwegian businesses and freelancers. Create fakturor (invoices) with 25% MVA auto-calculated, or standard invoices in NOK or EUR. Download a professional PDF instantly — no sign-up required for up to 5 invoices per month.',
      },
    },
  ],
};

const invoiceTypes = [
  {
    icon: Percent,
    title: 'MVA Invoice (Faktura)',
    subtitle: 'For MVA-registered Norwegian businesses',
    features: ['25% MVA auto-calculated', 'Organisasjonsnummer field', 'Net + MVA + total', 'Skatteetaten-compliant', 'NOK currency'],
    to: '/create/vat',
    color: 'red',
    btnClass: 'bg-red-600 hover:bg-red-700',
  },
  {
    icon: FileText,
    title: 'Standard Invoice',
    subtitle: 'For businesses below NOK 50,000',
    features: ['No MVA required', 'NOK currency', 'Custom line items', 'Logo upload', 'Payment terms'],
    to: '/create/freelancer',
    color: 'blue',
    btnClass: 'bg-blue-600 hover:bg-blue-700',
  },
  {
    icon: Globe,
    title: 'International Invoice',
    subtitle: 'For billing overseas clients',
    features: ['USD, EUR, GBP billing', 'Zero-rated export format', 'IBAN / SWIFT fields', 'Multi-currency', 'Professional PDF'],
    to: '/create/international',
    color: 'indigo',
    btnClass: 'bg-indigo-600 hover:bg-indigo-700',
  },
];

export default function InvoiceGeneratorNorwayPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Invoice Generator Norway: Free Faktura Maker for Norwegian Freelancers | Invilic"
        description="Free invoice generator (faktura maker) for Norwegian businesses and freelancers. Create MVA invoices with 25% VAT or standard fakturor in NOK. PDF download free, no sign-up."
        canonical="https://www.invilic.com/invoice-generator-norway"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <span className="inline-block bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            🇳🇴 Norway
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Invoice Generator Norway
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Free faktura (invoice) generator for Norwegian businesses, freelancers, and sole traders. Create MVA tax invoices with 25% merverdiavgift auto-calculated, or standard fakturor in NOK. Download professional PDF invoices instantly.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/create/vat" className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200">
              Create MVA Faktura <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/create/freelancer" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200">
              Create Standard Faktura <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">Which Invoice Do You Need?</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {invoiceTypes.map(({ icon: Icon, title, subtitle, features, to, color, btnClass }) => (
              <div key={title} className={`border border-${color}-100 dark:border-${color}-800/30 rounded-2xl p-6 bg-${color}-50 dark:bg-${color}-900/10`}>
                <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/40 flex items-center justify-center mb-4`}>
                  <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1">{title}</h3>
                <p className={`text-xs text-${color}-700 dark:text-${color}-300 mb-4`}>{subtitle}</p>
                <ul className="space-y-2 mb-5">
                  {features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <CheckCircle className={`w-3.5 h-3.5 text-${color}-500 flex-shrink-0`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={to} className={`inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 ${btnClass} text-white text-sm font-semibold rounded-xl transition-all`}>
                  Create Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16 p-8 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">MVA Faktura vs Standard Faktura in Norway</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="font-semibold text-red-700 dark:text-red-400 mb-3">Use an MVA Faktura if:</div>
              {[
                'Your annual taxable turnover exceeds NOK 50,000',
                'You are registered for MVA with Skatteetaten',
                'Your client needs a tax invoice showing MVA breakdown',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
            <div>
              <div className="font-semibold text-blue-700 dark:text-blue-400 mb-3">Use a Standard Faktura if:</div>
              {[
                'Your annual turnover is below NOK 50,000 (MVA exempt)',
                'You are a freelancer or sole trader not yet MVA-registered',
                'You are billing an overseas client (zero-rated export)',
              ].map(p => (
                <div key={p} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" /> {p}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Norway Invoice Generator: Frequently Asked Questions</h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Create Your Norwegian Faktura for Free</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">Professional PDF faktura for Norwegian businesses in under 2 minutes. No account required.</p>
          <Link to="/create/vat" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-red-600/20 transition-all duration-200 text-lg">
            Create Faktura Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}
